import { Component, inject } from '@angular/core';
import { ButtonsModule, ComponentsModule, DialogModule, DialogService, FormFieldModule, InputOption, ResultDataSource, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { ReactiveFormsModule } from '@angular/forms';
import { DialogRef } from '@angular/cdk/dialog';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { FormValidationService } from '@shared/services/form-validation.service';
import { addSystemAdminFormGroup, StoreSystemAdminRequest, SystemAdminSingleResponse, User } from '@lib/api/models/user';
import { UserService } from '@lib/user/user.service';
import { ResultObservable, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { map, shareReplay } from 'rxjs';

@Component({
    selector: 'app-add-system-admin-dialog',
    imports: [
        ButtonsModule,
        DialogModule,
        ErrorPanelComponent,
        FormFieldModule,
        ReactiveFormsModule,
        ComponentsModule
    ],
    templateUrl: './add-system-admin-dialog.component.html',
    standalone: true,
    styleUrl: './add-system-admin-dialog.component.scss'
})
export class AddSystemAdminDialogComponent {

    private readonly _snackBar = inject(SnackBarService);
    private readonly _formValidationService = inject(FormValidationService);
    private readonly _userService = inject(UserService);

    protected readonly systemAdminFormGroup = addSystemAdminFormGroup();

    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    protected readonly users$: ResultObservable<InputOption<User>[]> = this._userService.getAllPossibleUsers().pipe(
        map((response): InputOption<User>[] => {
            return (response?.data ?? []).map(user => ({
                value: user,
                label: `${user.person.given_name} ${user.person.family_name}`,
                testId: user.id
            }));
        }),
        toResult(),
        shareReplay(1)
    );

    protected searchableUsersDataSource = new ResultDataSource<User>({
        source: this.users$,
        searchable: true,
        compareValue: (a, b) => a.id === b.id
    });

    addSystemAdmin = new SendableRequest<SystemAdminSingleResponse, StoreSystemAdminRequest>({
        request: payload => this._userService.createSystemAdmin(payload!),

        onSuccess: () => {

            this._snackBar.open({
                colour: 'success',
                dismissable: false,
                icon: 'check_circle',
                title: 'System Admin created successfully',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });
            this.dialogRef.close();
        }
    });

    add() {
        if (!this.systemAdminFormGroup.valid) {

            this._formValidationService.markFormGroupTouched(this.systemAdminFormGroup);

            this._formValidationService.showFormErrors(this.systemAdminFormGroup, true);

            return;
        }

        const user = this.systemAdminFormGroup.controls.id.value as { id: string } | null;
        this.addSystemAdmin.send({
            id: user?.id || ''
        });
    }

    public static show(dialog: DialogService) {
        return dialog.openDialog<DialogRef>(AddSystemAdminDialogComponent, {});
    }
}
